// Formatting helpers. All of them follow the user's preferences (duration style,
// date/time format, currency) and the current language.

import { prefs, type DurationFormat } from './prefs.svelte';
import { i18n } from './i18n.svelte';
import type { TimeEntry } from './types';
import { autoClientColor, isHexColor } from './theme/palette';

// ---------- Dates ----------

/** Parse 'YYYY-MM-DD HH:MM:SS', 'YYYY-MM-DDTHH:MM' or 'YYYY-MM-DD' as local time. */
export function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  // ISO strings with a zone ('…Z', '+02:00') are absolute times, not local ones.
  if (/T\d{2}:\d{2}.*(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    const abs = new Date(value);
    if (!Number.isNaN(abs.getTime())) return abs;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
  if (m) {
    return new Date(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0, m[6] ? +m[6] : 0);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Local calendar date as 'YYYY-MM-DD'. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local 'HH:MM'. */
export function hhmm(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function today(): string {
  return isoDate(new Date());
}

export function formatDate(value: string | Date | null | undefined, style: 'pref' | 'short' | 'long' | 'weekday' | 'month' | 'day' = 'pref'): string {
  const d = parseDate(value as string);
  if (!d) return '—';
  const locale = i18n.locale;
  switch (style) {
    case 'short':
      return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    case 'long':
      return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    case 'weekday':
      return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    case 'month':
      return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    case 'day':
      return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    default: {
      const [dd, mm, yyyy] = [pad(d.getDate()), pad(d.getMonth() + 1), String(d.getFullYear())];
      switch (prefs.value.dateFormat) {
        case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
        case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
        case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
        default: return `${dd}-${mm}-${yyyy}`;
      }
    }
  }
}

export function formatTime(value: string | number | Date | null | undefined): string {
  const d = parseDate(value as string);
  if (!d) return '—';
  if (prefs.value.timeFormat === '12h') {
    const h = d.getHours() % 12 || 12;
    return `${h}:${pad(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
  }
  return hhmm(d);
}

export function formatRelative(value: string | number | Date | null | undefined): string {
  const d = parseDate(value as string);
  if (!d) return '—';
  const diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(i18n.locale, { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diff / (86400 * 30)), 'month');
  return rtf.format(Math.round(diff / (86400 * 365)), 'year');
}

/** ISO week number. */
export function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ---------- Durations ----------

/** Minutes → '7:56' | '7,93' | '7u 56m' (per preference). */
export function formatDuration(minutes: number | null | undefined, format: DurationFormat = prefs.value.durationFormat): string {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (format === 'decimal') {
    return (total / 60).toLocaleString(i18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (format === 'text') {
    const hu = i18n.lang === 'nl' ? 'u' : 'h';
    if (h === 0) return `${m}m`;
    return m ? `${h}${hu} ${m}m` : `${h}${hu}`;
  }
  return `${h}:${pad(m)}`;
}

/** Hours (decimal) → formatted like formatDuration. */
export function formatHours(hours: number | null | undefined, format?: DurationFormat): string {
  return formatDuration((Number(hours) || 0) * 60, format);
}

/** Milliseconds → 'HH:MM:SS' (or 'H:MM' without seconds). */
export function formatClock(ms: number, withSeconds = true): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 99) return withSeconds ? '99:59:59+' : '99:59+';
  return withSeconds ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${h}:${pad(m)}`;
}

// ---------- Numbers & money ----------

const moneyFormatters = new Map<string, Intl.NumberFormat>();

export function formatMoney(amount: number | null | undefined, opts: { compact?: boolean; decimals?: boolean } = {}): string {
  const value = Number(amount) || 0;
  const key = `${i18n.locale}|${prefs.value.currency}|${opts.compact ? 'c' : ''}|${opts.decimals === false ? 'n' : ''}`;
  let fmt = moneyFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(i18n.locale, {
      style: 'currency',
      currency: prefs.value.currency,
      notation: opts.compact ? 'compact' : 'standard',
      maximumFractionDigits: opts.decimals === false || opts.compact ? 0 : 2,
      minimumFractionDigits: opts.decimals === false || opts.compact ? 0 : 2
    });
    moneyFormatters.set(key, fmt);
  }
  return fmt.format(value);
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  return (Number(value) || 0).toLocaleString(i18n.locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatPercent(value: number | null | undefined, decimals = 0): string {
  return `${formatNumber(value, decimals)}%`;
}

/** Parse a user-typed amount: '75,50', '75.5', '€ 1.234,56'. */
export function parseAmount(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined || input === '') return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  let s = input.replace(/[^\d,.-]/g, '');
  if (s.includes(',') && s.includes('.')) {
    // The last separator is the decimal one.
    s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// ---------- Time input ----------

/** '9' → '09:00', '930' → '09:30', '0930' → '09:30', '9:5' → '09:05', '13.15' → '13:15'. */
export function parseTimeInput(input: string): string | null {
  const s = input.trim().replace(/[.,h]/gi, ':');
  let h: number;
  let m: number;
  if (s.includes(':')) {
    const [a, b] = s.split(':');
    h = parseInt(a, 10);
    m = parseInt(b || '0', 10);
  } else {
    const digits = s.replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length <= 2) {
      h = parseInt(digits, 10);
      m = 0;
    } else if (digits.length === 3) {
      h = parseInt(digits.slice(0, 1), 10);
      m = parseInt(digits.slice(1), 10);
    } else {
      h = parseInt(digits.slice(0, 2), 10);
      m = parseInt(digits.slice(2, 4), 10);
    }
  }
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

// ---------- Entries ----------

export function entryMinutes(e: Pick<TimeEntry, 'rounded_duration_minutes' | 'duration_minutes'>): number {
  return Number(e.rounded_duration_minutes ?? e.duration_minutes ?? 0) || 0;
}

export function entryAmount(e: Pick<TimeEntry, 'total_amount' | 'rate' | 'rounded_duration_minutes' | 'duration_minutes'>): number {
  if (e.total_amount !== null && e.total_amount !== undefined) return Number(e.total_amount) || 0;
  return (entryMinutes(e) / 60) * (Number(e.rate) || 0);
}

export function normalizeStatus(status: string | null | undefined): 'open' | 'partial' | 'paid' | 'non_billable' {
  const s = (status || 'open').toLowerCase();
  if (s === 'paid' || s === 'partial' || s === 'non_billable') return s;
  return 'open';
}

export function entryOutstanding(e: TimeEntry): number {
  const status = normalizeStatus(e.payment_status);
  if (status === 'paid' || status === 'non_billable') return 0;
  return Math.max(0, entryAmount(e) - (Number(e.amount_paid) || 0));
}

export function initials(name: string | null | undefined): string {
  const parts = (name || '?').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Stable colour for a client: its own metadata colour or an automatic one (lib/theme/palette.ts). */
export function clientColor(client: { id?: number | null; metadata?: { color?: string } | null } | null | undefined): string {
  const own = client?.metadata?.color;
  if (isHexColor(own)) return own;
  return autoClientColor(client?.id);
}
